import React from "react";
import styled from "styled-components";
import { LabelMedium } from "./OnboardingTypography";
import { THEME } from "./OnboardingLayoutComponents";

const OnboardingInputField = styled.input.withConfig({
  shouldForwardProp: (prop) => !['hasError'].includes(prop),
})`
  display: flex;
  padding: ${THEME.spacing.sm};
  color: ${THEME.colors.text};
  background: ${THEME.colors.white};
  border: 1px solid ${({ hasError }) => (hasError ? THEME.colors.error : THEME.colors.border)};
  border-radius: ${THEME.borderRadius.sm};
  margin-top: ${THEME.spacing.sm};
  transition: ${THEME.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? THEME.colors.error : THEME.colors.primary)};
  }
`;

export default OnboardingInputField;

const ErrorText = styled.span`
  color: ${THEME.colors.error};
  font-size: 16px;
  margin-top: ${THEME.spacing.xs};
`;

export const TooltipWrapper = styled.span`
  position: relative;
  display: inline-block;
`;

export const TooltipIcon = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${THEME.colors.background};
  color: ${THEME.colors.textSecondary};
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  margin-left: ${THEME.spacing.xs};
`;

export const TooltipText = styled.span`
  visibility: hidden;
  width: 160px;
  background-color: ${THEME.colors.textSecondary};
  color: ${THEME.colors.textLight};
  text-align: center;
  border-radius: ${THEME.borderRadius.md};
  padding: ${THEME.spacing.sm};
  position: absolute;
  z-index: 1;
  left: 110%;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  white-space: normal;
  transition: ${THEME.transitions.slow};

  ${TooltipWrapper}:hover & {
    visibility: visible;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OnboardingInput = React.forwardRef(({ label, error, type = "text", required = false, ...props }, ref) => {
  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${inputId}-error`;

  return (
    <InputContainer>
      {React.isValidElement(label) ? (
        // If label is a React component, render it directly (as a div)
        <div style={{
          fontFamily: 'Manrope',
          fontSize: '18px',
          fontWeight: '600',
          color: '#0d0d0d',
          lineHeight: '24px',
          margin: '0'
        }}>
          {label}
          {required && <span style={{ color: THEME.colors.error || "#d32f2f" }}> *</span>}
        </div>
      ) : (
        // If label is a string, render it in LabelMedium
        <LabelMedium>
          {label}
          {required && <span style={{ color: THEME.colors.error || "#d32f2f" }}> *</span>}
        </LabelMedium>
      )}
      <OnboardingInputField
        ref={ref}
        id={inputId}
        hasError={!!error}
        type={type}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        aria-required={required}
        {...props}
      />
      {error && <ErrorText id={errorId} role="alert">{error}</ErrorText>}
    </InputContainer>
  );
});

export { OnboardingInput };
